// src/Redux/Slice/TaskMasterSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';

// Base URL for your API
const BaseUrl = process.env.REACT_APP_API_URL;

// --- Asynchronous Thunks ---

/* Thunk to fetch customer support data from the API. */
export const fetchCustomerData = createAsyncThunk(
    'TaskMaster/fetchCustomerData',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BaseUrl}/CustomerSupport/wsetwertgvsdxsadf4556666`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// Add New Customer 
export const AddNewCustomer = createAsyncThunk(
    'customer/addCustomer',
    async (data, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BaseUrl}/CustomerSupport/dfghjhgfdsdfgmgvfcdxcv535`, data);
            if (response?.data.status === "Customer add") {
                toast.success("Member added successfully!");
            } else {
                toast.error("Failed to add member. Please try again.");
            }
            return response.data;
        } catch (error) {
            toast.error("Something went wrong while adding member.");
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// --- Add Client Thunk ---
export const addClient = createAsyncThunk(
    "Client/addClient",
    async ({ values, loginData }, { rejectWithValue }) => {
        const adminId = loginData?.data?._id;
        try {
            const response = await axios.post(`${BaseUrl}/client_add/${adminId}`, values);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Unknown error");
        }
    }
);

// Update Customer
export const UpdateCustomer = createAsyncThunk(
    'customer/updateCustomer',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BaseUrl}/CustomerSupport/safsddfgryrty45645654dfgdfg/${id}`, data);
            if (response?.data.status === "Customer update") {
                toast.success('Member updated successfully!');
            } else {
                toast.error('Failed to update member. Please try again.');
            }
            return response.data;
        } catch (error) {
            toast.error("Something went wrong while updating member.");
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// Turn ON/OFF Customer Status
export const CustomerOn = createAsyncThunk(
    "customer/onoffStatus",
    async (id, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BaseUrl}/CustomerSupport/DynamicCustomer_onoff/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

// Update Customer Priority
export const CustomerOnP = createAsyncThunk(
    "customer/onoffPriority",
    async (id, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BaseUrl}/CustomerSupport/DynamicCustomer_onoffPriority/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);


// --- Redux Slice ---

const AddCustomerSlice = createSlice({
    name: 'Addcustomer',
    initialState: {

        // Customer Data State
        CustomerData: [],
        customerLoading: false,
        customerError: null,

        ClientData: null,
        AddClientLoading: false,
        AddClientSuccess: false,
        AddClientError: null,

        // Optional: extra status fields
        createTaskStatus: 'idle',
        createTaskError: null,
        createdTask: null,

        addStatus: 'idle',
        addError: null,
    },
    reducers: {
        resetAddCustomerStatus: (state) => {
            state.addStatus = 'idle';
            state.addError = null;
        },
    },
    extraReducers: (builder) => {
        builder

            // --- Customer Data Handlers ---
            .addCase(fetchCustomerData.pending, (state) => {
                state.customerLoading = true;
                state.customerError = null;
            })
            .addCase(fetchCustomerData.fulfilled, (state, action) => {
                state.customerLoading = false;
                state.CustomerData = action.payload;
            })
            .addCase(fetchCustomerData.rejected, (state, action) => {
                state.customerLoading = false;
                state.customerError = action.payload;
            })

            // --- Add New Customer Data Handlers ---
            .addCase(AddNewCustomer.pending, (state) => {
                state.addStatus = 'loading';
                state.addError = null;
            })
            .addCase(AddNewCustomer.fulfilled, (state, action) => {
                state.addStatus = 'succeeded';
                state.CustomerData = action.payload;
            })
            .addCase(AddNewCustomer.rejected, (state, action) => {
                state.addStatus = 'failed';
                state.addError = action.payload;
            })

            // --- Add New Client Data Handlers 
            .addCase(addClient.pending, (state) => {
                state.AddClientLoading = true;
                state.AddClientSuccess = false;
                state.AddClientError = null;
                state.ClientData = null;
            })
            .addCase(addClient.fulfilled, (state, action) => {
                state.AddClientLoading = false;
                state.AddClientSuccess = true;
                state.AddClientError = null;
                state.ClientData = action.payload;
            })
            .addCase(addClient.rejected, (state, action) => {
                state.AddClientLoading = false;
                state.AddClientSuccess = false;
                state.AddClientError = action.payload || "Something went wrong";
                state.ClientData = null;
            })

            // --- Update Customer Handlers ---
            .addCase(UpdateCustomer.pending, (state) => {
                state.addStatus = 'loading';
                state.addError = null;
            })
            .addCase(UpdateCustomer.fulfilled, (state, action) => {
                state.addStatus = 'succeeded';
                state.CustomerData = action.payload;
            })
            .addCase(UpdateCustomer.rejected, (state, action) => {
                state.addStatus = 'failed';
                state.addError = action.payload;
            })

            // --- Customer ON/OFF Status ---
            .addCase(CustomerOn.fulfilled, (state, action) => {
                state.CustomerData = action.payload;
            })
            .addCase(CustomerOn.rejected, (state, action) => {
                state.customerError = action.payload;
            })

            // --- Customer Priority Update ---
            .addCase(CustomerOnP.fulfilled, (state, action) => {
                state.CustomerData = action.payload;
            })
            .addCase(CustomerOnP.rejected, (state, action) => {
                state.customerError = action.payload;
            })
    }
});

// Export any actions you define in `reducers`
export const { resetAddCustomerStatus } = AddCustomerSlice.actions;

// Export the reducer
export default AddCustomerSlice.reducer;